import { useEffect, useMemo, useRef, useState } from 'react'
import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import {
  filterInternalToolsByCategory,
  getVisibleInternalToolCategories,
  getVisibleInternalTools,
} from '@/lib/internalToolAccess'
import { internalToolCategories, internalTools } from '@/lib/internalToolRegistry'
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input } from '@/components/ui'
import { searchInternalTools } from '@/lib/internalToolSearch'
import { ToolTile } from './ToolTile'

export function ToolLauncher({ open, onOpenChange, user }) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const searchRef = useRef(null)
  const searchQuery = query.trim()
  const visibleTools = useMemo(() => getVisibleInternalTools(internalTools, user), [user])
  const visibleCategories = useMemo(
    () => getVisibleInternalToolCategories(visibleTools, internalToolCategories),
    [visibleTools],
  )
  const displayedTools = useMemo(() => {
    if (searchQuery) return searchInternalTools(visibleTools, searchQuery)

    return filterInternalToolsByCategory(visibleTools, category)
  }, [category, searchQuery, visibleTools])

  useEffect(() => {
    if (!open) return

    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(focusTimer)
  }, [open])

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setQuery('')
      setCategory('All')
    }
    onOpenChange(nextOpen)
  }

  const closeLauncher = () => handleOpenChange(false)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex h-dvh max-w-5xl flex-col gap-0 overflow-hidden border-border bg-card p-0 motion-reduce:animate-none motion-reduce:transition-none [&>button]:flex [&>button]:h-[44px] [&>button]:w-[44px] [&>button]:items-center [&>button]:justify-center sm:h-auto sm:max-h-screen"
        data-cy="tool-launcher"
      >
        <div className="border-b border-border px-5 pb-4 pt-5 pr-14 sm:px-6 sm:pb-5 sm:pt-6">
          <DialogTitle className="text-xl">All apps</DialogTitle>
          <DialogDescription className="mt-1">
            Find an app by name, team, or workflow.
          </DialogDescription>
          <div className="relative mt-4">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={searchRef}
              type="text"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                if (event.target.value.trim()) setCategory('All')
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && query) {
                  event.stopPropagation()
                  setQuery('')
                }
              }}
              placeholder="Try billing, Entrata, renewals, or resident outreach"
              aria-label="Search all apps"
              className="h-12 pl-12 pr-12 text-base"
              data-cy="tool-launcher-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  searchRef.current?.focus()
                }}
                aria-label="Clear search"
                className="absolute right-0.5 top-1/2 flex h-[44px] w-[44px] -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Cross2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="shrink-0 border-b border-border px-5 py-3 sm:px-6">
          <div className="flex min-w-0 gap-2 overflow-x-auto">
            {['All', ...visibleCategories].map((categoryName) => (
              <Button
                key={categoryName}
                type="button"
                variant={category === categoryName ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setCategory(categoryName)
                  setQuery('')
                }}
                aria-pressed={category === categoryName}
                className="h-[44px] shrink-0"
              >
                {categoryName}
              </Button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {displayedTools.length ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              {displayedTools.map((item) => (
                <ToolTile key={item.key} item={item} compact onSelect={closeLauncher} />
              ))}
            </div>
          ) : (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <p className="font-medium text-foreground">
                {visibleTools.length
                  ? `No apps found for “${searchQuery}”.`
                  : 'No apps are available for your account.'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {visibleTools.length
                  ? 'Try another app name, team, system, or workflow.'
                  : 'Ask an admin to update your app access.'}
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setQuery('')
                  setCategory('All')
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>

        <div className="hidden shrink-0 items-center justify-between border-t border-border px-6 py-3 text-xs text-muted-foreground sm:flex">
          <span aria-live="polite">
            {displayedTools.length} {displayedTools.length === 1 ? 'app' : 'apps'}
          </span>
          <span>Esc closes · Enter opens the selected app</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
